import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usersApi, mydataError } from "@/api";
import { useLang } from "@/i18n";

const EMPTY = { username: "", password: "", email: "", full_name: "", role: "user", modules: ["dashboard", "documents", "contacts"], active: true };

export const UserDialog = ({ open, user, modules, onClose, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isSuper = user?.role === "superadmin";

  useEffect(() => { if (open) setForm(user ? { ...EMPTY, ...user, password: "" } : EMPTY); }, [open, user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const toggleModule = (m) => setForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (user) {
        const payload = { email: form.email, full_name: form.full_name, modules: form.modules, active: form.active };
        if (!isSuper) payload.role = form.role;
        if (form.password) payload.password = form.password;
        await usersApi.update(user.id, payload);
      } else {
        await usersApi.create({ username: form.username, password: form.password, email: form.email, full_name: form.full_name, role: form.role, modules: form.modules });
      }
      toast.success(t("user_saved")); onSaved();
    } catch (err) { toast.error(mydataError(err, t)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-heading">{user ? t("edit_user") : t("new_user")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("username")}</Label>
              <Input value={form.username} onChange={set("username")} disabled={!!user} className="font-mono" data-testid="user-username-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{user ? t("new_password_optional") : t("password")}</Label>
              <Input type="password" value={form.password} onChange={set("password")} autoComplete="new-password" className="font-mono" data-testid="user-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("full_name")}</Label>
              <Input value={form.full_name} onChange={set("full_name")} data-testid="user-fullname-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} data-testid="user-email-input" />
            </div>
            {!isSuper && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t("role")}</Label>
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">{t("role_user")}</SelectItem>
                    <SelectItem value="admin">{t("role_admin")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {user && !isSuper && (
              <div className="flex items-center gap-2 pt-6">
                <Checkbox id="active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: !!v }))} data-testid="user-active-checkbox" />
                <Label htmlFor="active">{t("active")}</Label>
              </div>
            )}
          </div>
          {form.role === "user" && !isSuper && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t("modules_access")}</Label>
              <div className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 dark:border-slate-700 p-3">
                {modules.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.modules.includes(m)} onCheckedChange={() => toggleModule(m)} data-testid={`user-module-${m}`} />
                    {t(`nav_${m}`)}
                  </label>
                ))}
              </div>
            </div>
          )}
          {form.role === "admin" && <p className="text-xs text-slate-500">{t("admin_all_modules")}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="user-cancel-button">{t("cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-700" data-testid="user-submit-button">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
